import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { RenewalPolicy, SmsHistoryResponse } from '@/types/sms';
import { format } from 'date-fns';
import { getSmsHistory } from '@/services/api';
import { SmsHistoryModal } from './SmsHistoryModal';
import { PolicyLinkMetrics } from '@/components/sms/PolicyLinkMetrics';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface IndividualSmsGridProps {
  policies: RenewalPolicy[];
  selectedPolicyIds: string[];
  onToggleSelection: (policyNumber: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onTriggerSms: () => void;
  onSendToAll?: () => void;  // For bulk mode
  isIndividualSelectionMode?: boolean;  // Toggle between modes
  loading?: boolean;
  error?: string | null;
  pagination?: {
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  } | null;
  onPageChange: (page: number, pageSize: number) => void;
  onSearchChange?: (searchQuery: string) => void;  // Callback for search query changes
  totalCount?: number;  // Total count for bulk mode display (including CONVERTED)
  smsSendableCount?: number;  // Count excluding CONVERTED policies (for Send to All button)
}

export function IndividualSmsGrid({
  policies,
  selectedPolicyIds,
  onToggleSelection,
  onSelectAll,
  onClearSelection,
  onTriggerSms,
  onSendToAll,
  isIndividualSelectionMode = false,
  loading = false,
  error = null,
  pagination,
  onPageChange,
  onSearchChange,
  totalCount,
  smsSendableCount
}: IndividualSmsGridProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const onSearchChangeRef = useRef(onSearchChange);
  const isInitialMountRef = useRef(true);
  const [historyByPolicy, setHistoryByPolicy] = useState<Record<string, { data?: SmsHistoryResponse; loading: boolean; error?: string }>>({});
  const [openHistoryModal, setOpenHistoryModal] = useState<string | null>(null);
  const [linkMetricsPolicy, setLinkMetricsPolicy] = useState<string | null>(null);

  // Update ref when callback changes
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Helper functions
  const formatLob = (lob: string) => (lob?.toLowerCase() === 'motor' ? 'Motor' : 'Health');

  // Debounced search handler
  useEffect(() => {
    // Don't trigger search on initial mount if searchQuery is empty
    if (isInitialMountRef.current && !searchQuery.trim()) {
      isInitialMountRef.current = false;
      return;
    }
    isInitialMountRef.current = false;

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      if (onSearchChangeRef.current) {
        onSearchChangeRef.current(searchQuery.trim());
      }
    }, 400); // 400ms debounce delay

    // Cleanup on unmount or when searchQuery changes
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]); // Only depend on searchQuery

  // Use policies directly (no client-side filtering)
  const displayPolicies = policies;

  // Filter out CONVERTED policies for selection logic
  const selectablePolicies = displayPolicies.filter(p => p.lead_status !== 'CONVERTED');
  
  const allSelected = selectablePolicies.length > 0 && 
    selectablePolicies.every(p => selectedPolicyIds.includes(p.policy_number));
  const someSelected = selectablePolicies.some(p => selectedPolicyIds.includes(p.policy_number));

  const handleSelectAllClick = () => {
    if (allSelected) {
      onClearSelection();
    } else {
      // Only select non-CONVERTED policies
      const nonConvertedPolicyNumbers = selectablePolicies.map(p => p.policy_number);
      nonConvertedPolicyNumbers.forEach(policyNumber => {
        if (!selectedPolicyIds.includes(policyNumber)) {
          onToggleSelection(policyNumber);
        }
      });
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    onPageChange(newPage + 1, pagination?.page_size || 10);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPageChange(1, parseInt(event.target.value, 10));
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd-MM-yyyy');
    } catch {
      return dateString;
    }
  };

  const formatMobile = (mobile: string): string => {
    if (!mobile || mobile === 'N/A') return 'N/A';
    // Format mobile number for display (e.g., "12345 67890")
    if (mobile.length === 10) {
      return `${mobile.slice(0, 5)} ${mobile.slice(5)}`;
    }
    return mobile;
  };

  const getSmsStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status?.toLowerCase()) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      case 'queued':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    // Immediately trigger search with empty query
    if (onSearchChange) {
      onSearchChange('');
    }
  };

  const fetchSmsHistory = useCallback(async (policyNumber: string) => {
    const existing = historyByPolicy[policyNumber];
    if (existing?.data || existing?.loading) return;

    setHistoryByPolicy((prev) => ({
      ...prev,
      [policyNumber]: { ...existing, loading: true, error: undefined }
    }));

    try {
      const data = await getSmsHistory(policyNumber);
      setHistoryByPolicy((prev) => ({
        ...prev,
        [policyNumber]: { data, loading: false, error: undefined }
      }));
    } catch (err: any) {
      setHistoryByPolicy((prev) => ({
        ...prev,
        [policyNumber]: { data: existing?.data, loading: false, error: err?.message || 'Failed to load history' }
      }));
    }
  }, [historyByPolicy]);

  const handleOpenHistoryModal = (policyNumber: string) => {
    setOpenHistoryModal(policyNumber);
    fetchSmsHistory(policyNumber);
  };

  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(null);
  };

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{formatErrorForDisplay(error)}</Alert>
      </Paper>
    );
  }

  const isSearchActive = searchQuery.trim().length > 0;

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {/* Single Row: Title on Left, Search and Action Button on Right */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2 }}>
          {/* Left: Title and Count Chips */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
              {isIndividualSelectionMode ? 'Policy Selection' : 'Matching Policies'}
            </Typography>
            {isIndividualSelectionMode && selectedPolicyIds.length > 0 && (
              <Chip 
                label={`${selectedPolicyIds.length} selected`}
                color="primary"
                size="small"
              />
            )}
            {!isIndividualSelectionMode && totalCount !== undefined && (
              <Chip 
                label={`${totalCount} policies found`}
                color="primary"
                size="small"
                variant="outlined"
              />
            )}
            {isSearchActive && (
              <Chip 
                label={`${displayPolicies.length} filtered`}
                color="info"
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          {/* Right: Search Field and Action Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            <TextField
              size="small"
              placeholder="Search table data..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: '300px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {isIndividualSelectionMode ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={onTriggerSms}
                disabled={selectedPolicyIds.length === 0 || loading}
                sx={{ flexShrink: 0 }}
              >
                SMS Trigger ({selectedPolicyIds.length})
              </Button>
            ) : (
              onSendToAll && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={onSendToAll}
                  disabled={displayPolicies.length === 0 || loading || (smsSendableCount !== undefined ? smsSendableCount === 0 : (totalCount || displayPolicies.length) === 0)}
                  sx={{ flexShrink: 0 }}
                >
                  Send to All ({smsSendableCount !== undefined ? smsSendableCount : (totalCount || displayPolicies.length)})
                </Button>
              )
            )}
          </Box>
        </Box>

        {/* Table */}
        {loading && !policies.length ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading policies...
            </Typography>
          </Box>
        ) : displayPolicies.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">
              {isSearchActive 
                ? `No policies found matching "${searchQuery}". Try adjusting your search.`
                : 'No policies found. Try adjusting your filters.'}
            </Alert>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {isIndividualSelectionMode && (
                      <TableCell padding="checkbox" sx={{ backgroundColor: 'background.paper' }}>
                        <Checkbox
                          indeterminate={someSelected && !allSelected}
                          checked={allSelected}
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>Policy Number</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>RED</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>Customer Mobile</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>Registration No.</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>LOB</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>Channel</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>Sub-channel</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>Branch</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>Policy Status</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>SMS Status</TableCell>
                    <TableCell sx={{ backgroundColor: 'background.paper', fontWeight: 600 }}>Link Activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayPolicies.map((policy) => {
                    const isSelected = selectedPolicyIds.includes(policy.policy_number);
                    const isConverted = policy.lead_status === 'CONVERTED';
                    const isDisabled = isIndividualSelectionMode && isConverted;
                    return (
                      <TableRow
                        key={policy.policy_number}
                        hover={!isDisabled}
                        onClick={isIndividualSelectionMode && !isDisabled ? () => onToggleSelection(policy.policy_number) : undefined}
                        selected={isSelected}
                        sx={{
                          ...(isIndividualSelectionMode && !isDisabled ? { cursor: 'pointer' } : {}),
                          ...(isDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                        }}
                      >
                        {isIndividualSelectionMode && (
                          <TableCell padding="checkbox">
                            <Checkbox 
                              checked={isSelected} 
                              disabled={isDisabled}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {policy.policy_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(policy.red)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {formatMobile(policy.customer_mobile)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {policy.regn_no || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatLob(policy.lob)}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {policy.product_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {policy.channel || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {policy.sub_channel || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {policy.branch_office}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {policy.lead_status === 'CONVERTED' ? (
                            <Chip 
                              label="CONVERTED" 
                              size="small"
                              color="success"
                              variant="filled"
                            />
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {policy.sms_status ? (
                            <Chip 
                              label={policy.sms_status} 
                              size="small"
                              color={getSmsStatusColor(policy.sms_status)}
                              variant="filled"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenHistoryModal(policy.policy_number);
                              }}
                              sx={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <Chip label="No SMS" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLinkMetricsPolicy(policy.policy_number);
                            }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {pagination && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={pagination.total}
                rowsPerPage={pagination.page_size}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
              />
            )}
          </>
        )}

      {/* SMS History Modal */}
      {openHistoryModal && (
        <SmsHistoryModal
          open={!!openHistoryModal}
          onClose={handleCloseHistoryModal}
          policyNumber={openHistoryModal}
          history={historyByPolicy[openHistoryModal]?.data || null}
          loading={historyByPolicy[openHistoryModal]?.loading || false}
          error={historyByPolicy[openHistoryModal]?.error || null}
        />
      )}

      {/* Link Metrics Overlay (custom, not using MUI Dialog to avoid z-index conflicts) */}
      {linkMetricsPolicy && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000, // Increased from 2000 to be above all dropdowns (9999)
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto' // Explicitly ensure pointer events work
          }}
          onClick={() => setLinkMetricsPolicy(null)}
        >
          <Paper
            sx={{
              maxWidth: 720,
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              p: 3,
              boxShadow: 4,
              zIndex: 10001, // Ensure content is above backdrop
              pointerEvents: 'auto' // Ensure content is interactive
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                SMS Link Activity{linkMetricsPolicy ? ` – ${linkMetricsPolicy}` : ''}
              </Typography>
              <Button onClick={() => setLinkMetricsPolicy(null)} color="primary" size="small">
                Close
              </Button>
            </Box>
            <PolicyLinkMetrics policyNumber={linkMetricsPolicy} />
          </Paper>
        </Box>
      )}
    </Paper>
  );
}

